import { databaseService } from './DatabaseService';
import { databaseCachingService } from './DatabaseCachingService';
import { inspectionOrchestrationService } from '@/services/orchestration/InspectionOrchestrationService';
import { apiService } from '@/services/ApiService';
import { auth } from '@/services/UserAuthService';
import {
  Inspection,
  ChecklistItem,
  Attachment,
  InspectionResultStatus,
  TimeSensitivityStatus,
} from '@/types/model/inspection';
import {
  SyncComparisonResult,
  SyncStatus,
  FieldDiff,
  ChecklistItemDiff,
} from '@/types/model/sync';
import {
  InspectionSaveRequest,
  AttachmentRequest,
} from '@/types/request/InspectionRequests';

class SyncService {

  async fetchSyncComparisons(): Promise<SyncComparisonResult[]> {
    const contractId = auth.getCurrentContractId();
    const results: SyncComparisonResult[] = [];

    const cachedInspections = await databaseService.select(
      'cached_inspections',
      'contract_id = ?',
      [contractId]
    );

    if (cachedInspections.length === 0) {
      return [];
    }

    // Reconstruct local inspections from cache
    const localInspectionsMap = new Map<number, { inspection: Inspection; cachedAt: string; groupTitle: string }>();

    for (const cached of cachedInspections) {
      const checklistItems = await databaseService.select(
        'cached_checklist_items',
        'inspection_result_id = ?',
        [cached.inspection_result_id]
      );

      const processedChecklistItems: ChecklistItem[] = [];
      for (const item of checklistItems) {
        const attachments = await databaseService.select(
          'cached_attachments',
          'parent_type = ? AND parent_id = ?',
          ['checklist_item', item.checklist_item_id]
        );

        processedChecklistItems.push({
          Id: item.checklist_item_id,
          Order: item.order_index || 0,
          Text: item.text || '',
          Weight: item.weight || 1,
          Score: item.score,
          Comment: item.comment,
          ResultsAnalysis: item.results_analysis,
          AttachmentCount: attachments.length,
          Attachments: attachments.map((att: Record<string, unknown>) => ({
            Id: att.id as number,
            Description: (att.description as string) || '',
            FileId: (att.file_id as number) || 0,
            Filename: (att.file_name as string) || '',
            Link: att.link as string | undefined,
          })),
        });
      }

      const inspection: Inspection = {
        inspectionResultId: cached.inspection_result_id,
        lineItemId: cached.line_item_id || 0,
        lineItem: cached.line_item || '',
        title: cached.line_item_title || '',
        siteName: cached.site_name || '',
        siteId: cached.site_id || 0,
        isExceptional: cached.is_exceptional ? true : false,
        inspectedOn: cached.inspected_on || '',
        areaOfExpertise: cached.area_of_expertise || '',
        areaOfExpertiseId: cached.area_of_expertise_id || 0,
        userId: cached.user_id || 0,
        inspectorName: cached.inspector_name || '',
        compositeScore: cached.composite_score,
        timeSensitivity: (cached.time_sensitivity as TimeSensitivityStatus) || 'unscheduled',
        status: (cached.status as InspectionResultStatus) || 'not-started',
        checklistItems: processedChecklistItems,
        correctiveActions: null,
        inspectionPlanBackground: {
          LineItemPrimaryWeight: cached.line_item_primary_weight ?? null,
          LineItemAlternateWeight: cached.line_item_alternate_weight ?? null,
          TimeToInspect: cached.time_to_inspect ?? null,
          MethodOfInspection: (cached.method_of_inspection as string) || null,
          SampleSize: (cached.sample_size as string) || null,
          SampleSelection: (cached.sample_selection as string) || null,
          AdditionalInstructions: (cached.additional_instructions as string) || null,
          InspectionPlanAttachments: (cached.inspection_plan_attachments as Attachment[]) || [],
        },
        lineItemDetail: {
          MajorRequirement: (cached.major_requirement as string) || null,
          RelatedInfo: (cached.related_info as string) || null,
          EstimatedQuantity: (cached.estimated_quantity as string) || null,
          PerformanceStandard: (cached.performance_standard as string) || null,
          LineItemAttachments: (cached.line_item_attachments as Attachment[]) || [],
        },
      };

      localInspectionsMap.set(cached.inspection_result_id, {
        inspection,
        cachedAt: cached.cached_at || '',
        groupTitle: cached.group_title || '',
      });
    }

    // Fetch server data for matching IDs
    const ids = Array.from(localInspectionsMap.keys());
    let serverInspections: Inspection[] = [];
    try {
      serverInspections = await inspectionOrchestrationService.getInspections(ids);
    } catch (error) {
      console.error('Failed to fetch server inspections for comparison:', error);
      // If server fetch fails, all items are local-only
    }

    const serverMap = new Map<number, Inspection>();
    for (const si of serverInspections) {
      serverMap.set(si.inspectionResultId, si);
    }

    // Build comparison results
    for (const [irId, localData] of localInspectionsMap) {
      const serverInspection = serverMap.get(irId) || null;
      const localInspection = localData.inspection;

      let status: SyncStatus;
      let fieldDiffs: FieldDiff[] = [];
      let checklistDiffs: ChecklistItemDiff[] = [];
      let hasDifferences = false;

      if (!serverInspection) {
        status = 'local-only';
        hasDifferences = true;
      } else {
        const comparison = this.compareInspection(localInspection, serverInspection);
        fieldDiffs = comparison.fieldDiffs;
        checklistDiffs = comparison.checklistDiffs;
        hasDifferences = comparison.hasDifferences;
        status = hasDifferences ? 'conflict' : 'in-sync';
      }

      // Extract server updated timestamp from the inspection response data
      const serverUpdatedAt = serverInspection?.inspectedOn || null;

      results.push({
        inspectionResultId: irId,
        lineItem: localInspection.lineItem,
        lineItemTitle: localInspection.title,
        groupTitle: localData.groupTitle,
        localInspection,
        serverInspection,
        status,
        localCachedAt: localData.cachedAt,
        serverUpdatedAt,
        fieldDiffs,
        checklistDiffs,
        hasDifferences,
      });
    }

    // Sort: conflicts first, then local-only, then in-sync
    const statusOrder: Record<SyncStatus, number> = {
      'conflict': 0,
      'local-only': 1,
      'local-newer': 2,
      'server-newer': 3,
      'in-sync': 4,
    };
    results.sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);

    return results;
  }

  compareInspection(
    local: Inspection,
    server: Inspection
  ): { fieldDiffs: FieldDiff[]; checklistDiffs: ChecklistItemDiff[]; hasDifferences: boolean } {
    const fieldDiffs: FieldDiff[] = [];

    const addFieldDiff = (field: string, label: string, localVal: unknown, serverVal: unknown): void => {
      fieldDiffs.push({
        field,
        label,
        localValue: localVal,
        serverValue: serverVal,
        isDifferent: String(localVal ?? '') !== String(serverVal ?? ''),
      });
    };

    addFieldDiff('compositeScore', 'Composite Score', local.compositeScore, server.compositeScore);
    // addFieldDiff('status', 'Status', local.status, server.status);
    addFieldDiff('isExceptional', 'Exceptional', local.isExceptional, server.isExceptional);
    addFieldDiff('inspectedOn', 'Inspected On', local.inspectedOn, server.inspectedOn);
    addFieldDiff('inspectorName', 'Inspector', local.inspectorName, server.inspectorName);
    addFieldDiff('siteName', 'Site', local.siteName, server.siteName);

    // Compare checklist items
    const checklistDiffs: ChecklistItemDiff[] = [];
    const serverChecklistMap = new Map<number, ChecklistItem>();
    for (const item of server.checklistItems) {
      serverChecklistMap.set(item.Id, item);
    }

    for (const localItem of local.checklistItems) {
      const serverItem = serverChecklistMap.get(localItem.Id);
      if (!serverItem) {
        checklistDiffs.push({
          checklistItemId: localItem.Id,
          text: localItem.Text,
          scoreDiff: { field: 'Score', label: 'Score', localValue: localItem.Score, serverValue: null, isDifferent: true },
          commentDiff: { field: 'Comment', label: 'Comment', localValue: localItem.Comment, serverValue: null, isDifferent: true },
          attachmentCountDiff: { field: 'AttachmentCount', label: 'Attachments', localValue: localItem.AttachmentCount, serverValue: 0, isDifferent: localItem.AttachmentCount > 0 },
          attachmentNamesDiff: { field: 'AttachmentNames', label: 'Attachment Files', localValue: this.getAttachmentNames(localItem.Attachments), serverValue: '', isDifferent: true },
        });
        continue;
      }

      const scoreDiff: FieldDiff = {
        field: 'Score',
        label: 'Score',
        localValue: localItem.Score,
        serverValue: serverItem.Score,
        isDifferent: localItem.Score !== serverItem.Score,
      };

      const commentDiff: FieldDiff = {
        field: 'Comment',
        label: 'Comment',
        localValue: localItem.Comment,
        serverValue: serverItem.Comment,
        isDifferent: (localItem.Comment || '') !== (serverItem.Comment || ''),
      };

      const localAttCount = localItem.Attachments?.length || 0;
      const serverAttCount = serverItem.Attachments?.length || 0;
      const attachmentCountDiff: FieldDiff = {
        field: 'AttachmentCount',
        label: 'Attachments',
        localValue: localAttCount,
        serverValue: serverAttCount,
        isDifferent: localAttCount !== serverAttCount,
      };

      const localNames = this.getAttachmentNames(localItem.Attachments);
      const serverNames = this.getAttachmentNames(serverItem.Attachments);
      const attachmentNamesDiff: FieldDiff = {
        field: 'AttachmentNames',
        label: 'Attachment Files',
        localValue: localNames,
        serverValue: serverNames,
        isDifferent: localNames !== serverNames,
      };

      if (scoreDiff.isDifferent || commentDiff.isDifferent || attachmentCountDiff.isDifferent || attachmentNamesDiff.isDifferent) {
        checklistDiffs.push({
          checklistItemId: localItem.Id,
          text: serverItem.Text,
          scoreDiff,
          commentDiff,
          attachmentCountDiff,
          attachmentNamesDiff,
        });
      }
    }

    const hasDifferences = fieldDiffs.some(d => d.isDifferent) || checklistDiffs.length > 0;

    return { fieldDiffs, checklistDiffs, hasDifferences };
  }

  private getAttachmentNames(attachments: Attachment[] | null): string {
    if (!attachments || attachments.length === 0) return '';
    return attachments.map(a => a.Filename || a.Link || '').filter(Boolean).join(', ');
  }

  async commitLocalToServer(
    localInspection: Inspection,
    serverInspection: Inspection
  ): Promise<void> {
    // Build delta request treating server data as baseline and local as desired state
    const checklistItems = localInspection.checklistItems.map(localItem => {
      const serverItem = serverInspection.checklistItems.find(si => si.Id === localItem.Id);

      const added: AttachmentRequest[] = [];
      const deleted: number[] = [];

      // Attachments added locally (present in local but not in server)
      if (localItem.Attachments) {
        for (const localAtt of localItem.Attachments) {
          const existsOnServer = serverItem?.Attachments?.find(sa => sa.FileId === localAtt.FileId && sa.FileId !== 0);
          if (!existsOnServer) {
            added.push({
              Id: undefined,
              Description: localAtt.Description || '',
              FileId: localAtt.FileId || undefined,
              Filename: localAtt.Filename || undefined,
              Link: localAtt.Link || undefined,
            });
          }
        }
      }

      // Attachments deleted locally (present on server but not in local)
      if (serverItem?.Attachments) {
        for (const serverAtt of serverItem.Attachments) {
          const existsLocally = localItem.Attachments?.find(la => la.FileId === serverAtt.FileId && la.FileId !== 0);
          if (!existsLocally) {
            deleted.push(serverAtt.Id);
          }
        }
      }

      return {
        Id: localItem.Id,
        Score: localItem.Score ?? null,
        Comment: localItem.Comment || null,
        Attachments: { Add: added, Edit: [], Delete: deleted },
      };
    });

    const request: InspectionSaveRequest = {
      Id: localInspection.inspectionResultId,
      Comments: '',
      LineItemId: localInspection.lineItemId,
      SiteId: localInspection.siteId,
      UserId: localInspection.userId || null,
      IsExceptional: localInspection.isExceptional,
      InspectedOn: localInspection.inspectedOn,
      ChecklistItems: checklistItems,
      Attachments: { Add: [], Edit: [], Delete: [] },
      AlertDays: 0,
      AlertEmails: '',
      IsInProgress: localInspection.status === 'in-progress',
    };

    await apiService.saveInspection(request);

    // Update local cache timestamp after successful commit
    await databaseService.update(
      'cached_inspections',
      { cached_at: new Date().toISOString() },
      'inspection_result_id = ?',
      [localInspection.inspectionResultId]
    );
    databaseCachingService.notifyCacheChanged();
  }

  async overrideLocalWithServer(inspectionResultId: number): Promise<void> {
    // Fetch fresh server data
    const serverInspections = await inspectionOrchestrationService.getInspections([inspectionResultId]);
    if (serverInspections.length === 0) {
      throw new Error(`Inspection ${inspectionResultId} not found on server`);
    }
    const serverInspection = serverInspections[0];

    // Get the cached inspection row to preserve group metadata
    const cachedRows = await databaseService.select(
      'cached_inspections',
      'inspection_result_id = ?',
      [inspectionResultId]
    );

    if (cachedRows.length === 0) {
      throw new Error(`Local cache entry for inspection ${inspectionResultId} not found`);
    }

    const cached = cachedRows[0];

    // Delete and re-insert the inspection row with server data
    await databaseService.delete('cached_inspections', 'inspection_result_id = ?', [inspectionResultId]);
    await databaseService.insert('cached_inspections', {
      inspection_result_id: serverInspection.inspectionResultId,
      contract_id: cached.contract_id,
      group_id: cached.group_id,
      group_identifier: cached.group_identifier,
      group_title: cached.group_title,
      line_item_id: serverInspection.lineItemId,
      line_item_title: serverInspection.title,
      line_item: serverInspection.lineItem,
      site_id: serverInspection.siteId,
      site_name: serverInspection.siteName,
      user_id: serverInspection.userId,
      inspector_name: serverInspection.inspectorName,
      area_of_expertise: serverInspection.areaOfExpertise,
      area_of_expertise_id: serverInspection.areaOfExpertiseId,
      composite_score: serverInspection.compositeScore,
      time_sensitivity: cached.timeSensitivity,
      status: cached.status,
      inspected_on: serverInspection.inspectedOn,
      is_exceptional: serverInspection.isExceptional,
      cached_at: new Date().toISOString(),
    });

    // Replace checklist items and attachments with server data
    await databaseCachingService.updateCachedInspection(serverInspection);
  }

  async deleteLocalInspection(inspectionResultId: number): Promise<void> {
    // Delete checklist item attachments
    const checklistItems = await databaseService.select(
      'cached_checklist_items',
      'inspection_result_id = ?',
      [inspectionResultId]
    );

    for (const item of checklistItems) {
      await databaseService.delete(
        'cached_attachments',
        'parent_type = ? AND parent_id = ?',
        [`checklist_item`, item.checklist_item_id]
      );
    }

    await databaseService.delete('cached_attachments','parent_type = ? AND parent_id = ?', [`inspection`, inspectionResultId]);
    await databaseService.delete('cached_checklist_items', 'inspection_result_id = ?', [inspectionResultId]);
    await databaseService.delete('cached_inspections', 'inspection_result_id = ?', [inspectionResultId]);
  }

  async clearAllCachedData(): Promise<void> {
    await databaseCachingService.clearCache(false);
  }

  async clearCachedContractData(): Promise<void> {
    const contractId = auth.getCurrentContractId();
    if (contractId === null) throw new Error('No contract selected');
    await databaseCachingService.clearCachedContractInspections(contractId);
  }
}

export const syncService = new SyncService();
export default syncService;
